import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Card,
  CardContent,
  Chip,
  Link,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { useClassroom } from "../../../context/ClassroomContext";
import {
  getClassroom,
  getQuizResults,
} from "../../../services/classroomService";
import { useHandleError } from "../../../hooks/useHandleError";
import { formatModuleLabel, getModuleName } from "../../../utils/moduleUtils";

interface QuizSummaryRow {
  studentId: string;
  studentName: string;
  studentEmail: string;
  moduleId: string;
  bestScore: number;
  attempts: number;
  lastAttemptDate: number;
  earnedPoints: number;
  totalPoints: number;
}

const scoreChip = (score: number) => {
  const color = score >= 80 ? "success" : score >= 50 ? "warning" : "error";
  return (
    <Chip
      label={`${Math.round(score)}%`}
      color={color}
      size="small"
      sx={{ fontSize: ".7rem" }}
    />
  );
};

const QuizResults: React.FC = () => {
  const { classroomId } = useParams<{ classroomId: string }>();
  const navigate = useNavigate();
  const handleError = useHandleError();
  const { classroom, setClassroom } = useClassroom();

  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState<QuizSummaryRow[]>([]);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (!classroomId) return;
    if (!classroom || classroom._id !== classroomId) {
      getClassroom(classroomId)
        .then((res) => res.data && setClassroom(res.data))
        .catch(handleError);
    }
  }, [classroomId]);

  useEffect(() => {
    if (!classroomId) return;
    setLoading(true);
    getQuizResults(classroomId)
      .then((res) => setSummary(res.data?.summary || []))
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [classroomId]);

  // Compute stats
  const totalSubmissions = summary.reduce((acc, s) => acc + s.attempts, 0);
  const avgScore =
    summary.length > 0
      ? summary.reduce((acc, s) => acc + s.bestScore, 0) / summary.length
      : 0;
  const uniqueStudents = new Set(summary.map((s) => s.studentId)).size;
  const totalStudents = classroom?.students?.length || 0;

  // Module stats
  const moduleMap = new Map<string, { totalScore: number; count: number; attempts: number }>();
  for (const s of summary) {
    if (!moduleMap.has(s.moduleId)) {
      moduleMap.set(s.moduleId, { totalScore: 0, count: 0, attempts: 0 });
    }
    const m = moduleMap.get(s.moduleId)!;
    m.totalScore += s.bestScore;
    m.count++;
    m.attempts += s.attempts;
  }
  const moduleStats = Array.from(moduleMap.entries()).map(([moduleId, m]) => ({
    moduleId,
    avgScore: m.count > 0 ? m.totalScore / m.count : 0,
    students: m.count,
    totalAttempts: m.attempts,
  }));
  const hardestModule = moduleStats.length > 0
    ? moduleStats.reduce((min, m) => (m.avgScore < min.avgScore ? m : min))
    : null;

  // By-student columns
  const studentColumns: GridColDef[] = [
    { field: "studentName", headerName: "Student", width: 180 },
    { field: "studentEmail", headerName: "Email", width: 220 },
    {
      field: "moduleId",
      headerName: "Module",
      width: 250,
      valueGetter: (params) => formatModuleLabel(params.value),
    },
    {
      field: "bestScore",
      headerName: "Best Score",
      width: 110,
      renderCell: (params: GridRenderCellParams) => scoreChip(params.value),
    },
    { field: "attempts", headerName: "Attempts", width: 90, align: "center", headerAlign: "center" },
    {
      field: "earnedPoints",
      headerName: "Points",
      width: 100,
      valueGetter: (params) => `${params.row.earnedPoints}/${params.row.totalPoints}`,
    },
    {
      field: "lastAttemptDate",
      headerName: "Last Attempt",
      width: 130,
      renderCell: (params: GridRenderCellParams) => {
        if (!params.value) return "";
        return new Date(params.value).toLocaleDateString("en-AU");
      },
    },
  ];

  // By-module columns
  const moduleColumns: GridColDef[] = [
    {
      field: "moduleId",
      headerName: "Module",
      width: 300,
      valueGetter: (params) => formatModuleLabel(params.value),
    },
    {
      field: "avgScore",
      headerName: "Class Average",
      width: 130,
      renderCell: (params: GridRenderCellParams) => scoreChip(params.value),
    },
    { field: "students", headerName: "Students", width: 100, align: "center", headerAlign: "center" },
    { field: "totalAttempts", headerName: "Total Attempts", width: 130, align: "center", headerAlign: "center" },
  ];

  return (
    <Box sx={{ p: 2 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="inherit"
          sx={{ cursor: "pointer" }}
          onClick={() => navigate("/teacher/classrooms")}
        >
          Classrooms
        </Link>
        <Typography color="text.primary">
          {classroom?.classroomName || "..."} — Quiz Results
        </Typography>
      </Breadcrumbs>

      <Typography variant="h5" gutterBottom>
        Quiz Results
      </Typography>

      {/* Summary cards */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <Card sx={{ minWidth: 150 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary">Class Average</Typography>
            <Typography variant="h5">{Math.round(avgScore)}%</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 150 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary">Total Submissions</Typography>
            <Typography variant="h5">{totalSubmissions}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 150 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary">Completion Rate</Typography>
            <Typography variant="h5">
              {totalStudents > 0 ? Math.round((uniqueStudents / totalStudents) * 100) : 0}%
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 150 }}>
          <CardContent>
            <Typography variant="body2" color="textSecondary">Hardest Module</Typography>
            <Typography variant="body1" noWrap>
              {hardestModule ? getModuleName(hardestModule.moduleId) : "N/A"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {hardestModule ? `${Math.round(hardestModule.avgScore)}% avg` : ""}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="By Student" />
        <Tab label="By Module" />
      </Tabs>

      {tab === 0 && (
        <DataGrid
          rows={summary}
          columns={studentColumns}
          getRowId={(row) => `${row.studentId}-${row.moduleId}`}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50]}
        />
      )}

      {tab === 1 && (
        <DataGrid
          rows={moduleStats}
          columns={moduleColumns}
          getRowId={(row) => row.moduleId}
          loading={loading}
          autoHeight
          disableRowSelectionOnClick
          initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
          pageSizeOptions={[10, 25, 50]}
        />
      )}
    </Box>
  );
};

export default QuizResults;
